import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import CloseIcon from "@material-ui/icons/Close";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import EventIcon from "@material-ui/icons/Event";
import ScheduleIcon from "@material-ui/icons/Schedule";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import AddIcon from "@material-ui/icons/Add";
import { Can } from "../Can";
import InputAdornment from "@material-ui/core/InputAdornment";
import Avatar from "@material-ui/core/Avatar";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { poderDeAdmin } from "../../utils/perfil";

const VERDE = "#1E9855";
const VERDE_CLARO = "#34d399";

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.07)";
  const vidro = escuro ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.85)";

  return {
    dialogPaper: {
      borderRadius: 24,
      boxShadow: escuro
        ? "0 30px 80px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(52, 211, 153, 0.12)"
        : "0 30px 80px rgba(15, 23, 42, 0.22)",
      backgroundColor: escuro ? "#111614" : "#f4f7fb",
      maxWidth: 880,
      width: "100%",
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },

    // ── Topo (hero) ─────────────────────────────────────────────
    hero: {
      position: "relative",
      padding: theme.spacing(3, 3, 2.5, 3),
      // mesmo estilo do banner do Dashboard: azul-noite com brilho verde
      background:
        "radial-gradient(420px 200px at 85% 0%, rgba(34,197,94,0.30), transparent 70%)," +
        "radial-gradient(360px 180px at 0% 100%, rgba(34,197,94,0.14), transparent 70%)," +
        "linear-gradient(120deg, #111816 0%, #151d1a 55%, #10261d 100%)",
      color: "#fff",
      flexShrink: 0,
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        width: 320,
        height: 320,
        right: -90,
        top: -160,
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 70%)",
      },
      "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
        maskImage: "linear-gradient(90deg, transparent 0%, #000 60%)",
        WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 60%)",
        pointerEvents: "none",
      },
    },
    heroTop: {
      position: "relative",
      zIndex: 1,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
    },
    avatar: {
      width: 64,
      height: 64,
      fontSize: "1.6rem",
      fontWeight: 800,
      color: VERDE,
      backgroundColor: "#fff",
      boxShadow: "0 0 0 3px rgba(255,255,255,0.35), 0 8px 24px rgba(0,0,0,0.25)",
    },
    heroTexto: { flex: 1, minWidth: 0 },
    heroEtiqueta: {
      fontSize: "0.68rem",
      fontWeight: 700,
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      opacity: 0.8,
    },
    heroNome: {
      fontSize: "1.45rem",
      fontWeight: 800,
      lineHeight: 1.2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    heroChips: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 8,
    },
    heroChip: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: "0.76rem",
      fontWeight: 600,
      backgroundColor: "rgba(255,255,255,0.16)",
      border: "1px solid rgba(255,255,255,0.25)",
      backdropFilter: "blur(6px)",
      "& svg": { fontSize: "0.95rem" },
    },
    closeBtn: {
      position: "relative",
      zIndex: 1,
      color: "#fff",
      backgroundColor: "rgba(255,255,255,0.14)",
      "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
    },

    // ── Resumo (cartoes de numeros) ─────────────────────────────
    statsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 12,
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
    },
    statCard: {
      position: "relative",
      borderRadius: 16,
      padding: "12px 14px",
      backgroundColor: vidro,
      border: `1px solid ${borda}`,
      boxShadow: escuro ? "none" : "0 6px 18px rgba(15, 23, 42, 0.05)",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        background: `linear-gradient(180deg, ${VERDE_CLARO}, ${VERDE})`,
      },
    },
    statLabel: {
      fontSize: "0.66rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    statValue: {
      marginTop: 4,
      fontSize: "1.05rem",
      fontWeight: 800,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    statVazio: { color: theme.palette.text.disabled, fontWeight: 600 },

    // ── Corpo ───────────────────────────────────────────────────
    dialogContent: {
      padding: theme.spacing(2.5, 3),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      overflowY: "auto",
      overscrollBehavior: "contain",
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: escuro ? "rgba(255, 255, 255, 0.18)" : "rgba(30, 152, 85, 0.3)",
        borderRadius: 10,
      },
      "&::-webkit-scrollbar-thumb:hover": { backgroundColor: VERDE },
    },
    sectionCard: {
      borderRadius: 18,
      padding: theme.spacing(2.25, 2.5),
      animation: "iaSubir .35s ease backwards",
      backgroundColor: vidro,
      border: `1px solid ${borda}`,
      boxShadow: escuro ? "none" : "0 6px 20px rgba(15, 23, 42, 0.05)",
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.25),
      marginBottom: theme.spacing(1.75),
    },
    sectionIconBox: {
      width: 30,
      height: 30,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      background: `linear-gradient(135deg, ${VERDE_CLARO}, ${VERDE})`,
      boxShadow: "0 4px 12px rgba(30, 152, 85, 0.35)",
    },
    sectionIcon: { fontSize: "1.05rem" },
    sectionTitle: {
      fontSize: "0.95rem",
      fontWeight: 800,
      letterSpacing: 0,
      color: theme.palette.text.primary,
    },
    customInput: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        backgroundColor: escuro ? "rgba(255,255,255,0.03)" : "#fff",
        fontSize: "0.88rem",
        transition: "box-shadow .2s ease",
        "& fieldset": { borderColor: escuro ? "rgba(255, 255, 255, 0.12)" : "#e2e8f0" },
        "&:hover fieldset": { borderColor: VERDE },
        "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(30, 152, 85, 0.12)" },
        "&.Mui-focused fieldset": { borderColor: VERDE, borderWidth: "1.5px" },
      },
      "& .MuiInputLabel-outlined": {
        fontSize: "0.86rem",
        "&.Mui-focused": { color: VERDE },
      },
    },
    inputAdornmentIcon: { color: theme.palette.text.secondary, fontSize: "1.05rem" },

    // ── Simulacoes ──────────────────────────────────────────────
    simBox: {
      marginTop: 16,
      borderRadius: 14,
      border: `1px solid ${borda}`,
      overflow: "hidden",
    },
    simTitle: {
      fontSize: "0.7rem",
      fontWeight: 800,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      padding: "10px 14px",
      backgroundColor: escuro ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
    },
    simTable: {
      width: "100%",
      borderCollapse: "collapse",
      "& th": {
        fontSize: "0.66rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: theme.palette.text.secondary,
        padding: "8px 14px",
        textAlign: "left",
        whiteSpace: "nowrap",
      },
      "& td": {
        fontSize: "0.82rem",
        color: theme.palette.text.primary,
        padding: "9px 14px",
        borderTop: `1px solid ${borda}`,
        whiteSpace: "nowrap",
      },
      "& tbody tr": { transition: "background-color .15s ease" },
      "& tbody tr:hover": {
        backgroundColor: escuro ? "rgba(255,255,255,0.03)" : "rgba(30,152,85,0.04)",
      },
    },
    simEscolhido: {
      backgroundColor: escuro ? "rgba(30, 152, 85, 0.16)" : "rgba(30, 152, 85, 0.08)",
      fontWeight: 700,
    },
    simTag: {
      marginLeft: 6,
      fontSize: "0.62rem",
      fontWeight: 800,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: "#fff",
      background: `linear-gradient(135deg, ${VERDE_CLARO}, ${VERDE})`,
      borderRadius: 999,
      padding: "2px 7px",
    },

    // ── Informacoes adicionais ──────────────────────────────────
    extraInfoRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    btnAddExtra: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 700,
      border: "1px dashed rgba(30, 152, 85, 0.5)",
      color: VERDE,
      backgroundColor: "rgba(30, 152, 85, 0.05)",
      padding: theme.spacing(0.9, 2),
      width: "100%",
      marginTop: theme.spacing(1),
      "&:hover": { backgroundColor: "rgba(30, 152, 85, 0.12)", borderColor: VERDE },
    },
    deleteBtn: {
      color: "#ef4444",
      padding: 6,
      "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.08)" },
    },

    // ── Rodape ──────────────────────────────────────────────────
    btnExcluir: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 700,
      color: "#ef4444",
      padding: "8px 14px",
      "&:hover": { backgroundColor: "rgba(239,68,68,0.08)" },
    },
    btnExcluirConfirma: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 800,
      color: "#fff",
      backgroundColor: "#ef4444",
      padding: "8px 14px",
      animation: "iaBalancar .5s ease",
      "&:hover": { backgroundColor: "#dc2626" },
    },
    dialogActions: {
      padding: theme.spacing(1.5, 3),
      borderTop: `1px solid ${borda}`,
      backgroundColor: escuro ? "rgba(255,255,255,0.02)" : "#fff",
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(1.5),
      flexShrink: 0,
    },
    btnCancel: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 600,
      padding: "8px 20px",
      color: theme.palette.text.secondary,
      borderColor: escuro ? "rgba(255, 255, 255, 0.15)" : "#d0d5dd",
      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)", borderColor: theme.palette.text.primary },
    },
    btnSave: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 700,
      padding: "8px 26px",
      color: "#fff",
      background: `linear-gradient(135deg, ${VERDE} 0%, #0ea5a4 100%)`,
      boxShadow: "0 6px 18px rgba(30, 152, 85, 0.35)",
      "&:hover": { boxShadow: "0 8px 24px rgba(30, 152, 85, 0.45)", filter: "brightness(1.05)" },
    },
    btnWrapper: { position: "relative" },
    buttonProgress: {
      color: "#ffffff",
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
  };
});

const maskCPF = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "").slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};

const maskPhone = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length >= 12) {
    const local = digits.substring(2);
    if (local.length === 11) {
      return `+55 (${local.substring(0, 2)}) ${local.substring(2, 7)}-${local.substring(7)}`;
    } else if (local.length === 10) {
      return `+55 (${local.substring(0, 2)}) ${local.substring(2, 6)}-${local.substring(6)}`;
    }
    return `+55 ${local}`;
  }
  if (digits.length === 11) {
    return `(${digits.substring(0, 2)}) ${digits.substring(2, 7)}-${digits.substring(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.substring(0, 2)}) ${digits.substring(2, 6)}-${digits.substring(6)}`;
  }
  return value;
};

const maskDate = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "").slice(0, 8);
  return digits
    .replace(/(\d{2})(\d)/, "$1/$2")
    .replace(/(\d{2})(\d)/, "$1/$2");
};

const maskMoney = (value) => {
  if (!value) return "";
  let v = String(value).replace(/\D/g, "");
  if (!v) return "";
  v = (Number(v) / 100).toFixed(2).replace(".", ",");
  v = v.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `R$ ${v}`;
};

// Valor que VEM do banco de dados: pode chegar como numero cru
// ("4003.073479794035", da IA) ou ja formatado ("R$ 4.003,07", da tela).
// O maskMoney acima nao serve aqui: ele e mascara de DIGITACAO, le so os
// digitos e divide por 100 — num numero cru isso vira valor absurdo.
const moedaDoRegistro = (valor) => {
  if (valor === null || valor === undefined) return "";

  const texto = String(valor).trim();
  if (!texto || texto === "-") return "";

  let limpo = texto.replace(/[^\d,.-]/g, "");
  if (!limpo) return "";

  if (limpo.includes(",")) {
    // "R$ 4.003,07" -> ponto e separador de milhar
    limpo = limpo.replace(/\./g, "").replace(",", ".");
  } else if ((limpo.match(/\./g) || []).length > 1) {
    // Sem virgula o texto veio como numero cru do motor, e ali o ponto e
    // SEMPRE decimal ("4003.073479794035"). So tratamos ponto como milhar
    // quando ha mais de um — "1.234.567". A regra antiga olhava "3 digitos
    // depois do ponto" e lia "1234.567" como 1.234.567.
    limpo = limpo.replace(/\./g, "");
  }

  const n = Number(limpo);
  if (!Number.isFinite(n) || n <= 0) return "";

  return `R$ ${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const maskNumber = (value) => {
  if (!value) return "";
  return String(value).replace(/\D/g, "");
};

const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Nome muito curto")
    .max(80, "Nome muito longo")
    .required("Obrigatório"),
  number: Yup.string().min(8, "Número inválido").max(50, "Número inválido"),
  email: Yup.string().email("Email inválido"),
});

const extrairSimulacoes = (extraInfo = []) => {
  const ehJson = (nome) =>
    nome.endsWith("simulações (json)") || nome.endsWith("simulacoes (json)");

  const ocultos = extraInfo.filter((i) =>
    ehJson((i.name || "").toLowerCase().trim())
  );

  const linhas = [];

  ocultos.forEach((info) => {
    try {
      const lista = JSON.parse(info.value || "[]");
      if (Array.isArray(lista)) {
        lista.forEach((o) => {
          if (o && o.banco) {
            linhas.push({
              banco: String(o.banco).trim(),
              tabela: o.tabela ? String(o.tabela).trim() : "",
              valor: o.valor ? String(o.valor).trim() : "",
              parcela: o.parcela ? String(o.parcela).trim() : "",
              prazo: o.prazo ? String(o.prazo).trim() : "",
              empregador: o.empregador ? String(o.empregador).trim() : "",
              escolhida: o.escolhida === true,
            });
          }
        });
      }
    } catch (e) {
      
    }
  });

  if (linhas.length === 0) {
    const texto = extraInfo.find((i) =>
      (i.name || "").toLowerCase().includes("bancos com oferta")
    )?.value;

    if (texto && !texto.toLowerCase().includes("nenhum")) {
      texto
        .split(/,\s*(?=[A-Za-zÀ-ÿ])/)
        .map((t) => t.trim())
        .filter(Boolean)
        .forEach((item) => {
          const casa = item.match(/^(.*?)\s*\((.*)\)\s*$/);
          linhas.push({
            banco: (casa ? casa[1] : item).trim(),
            valor: casa ? casa[2].trim() : "",
            parcela: "",
            prazo: "",
            empregador: "",
          });
        });
    }
  }

  const vistos = new Map();
  linhas.forEach((l) => vistos.set(l.banco.toLowerCase(), l));

  return { simulacoes: [...vistos.values()], ocultos };
};

const comMoeda = (valor) => {
  const texto = String(valor || "").trim();
  if (!texto) return "—";
  return texto.toUpperCase().includes("R$") ? texto : `R$ ${texto}`;
};

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  // Excluir pelo proprio modal: 1o clique arma, 2o clique (em ate 5s) exclui.
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  useEffect(() => {
    if (!confirmarExclusao) return undefined;
    const t = setTimeout(() => setConfirmarExclusao(false), 5000);
    return () => clearTimeout(t);
  }, [confirmarExclusao]);
  useEffect(() => {
    if (!open) setConfirmarExclusao(false);
  }, [open]);

  const excluirContato = async () => {
    if (!contactId) return;
    if (!confirmarExclusao) {
      setConfirmarExclusao(true);
      return;
    }
    setExcluindo(true);
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success("Contato excluído.");
      setConfirmarExclusao(false);
      onClose();
    } catch (err) {
      toastError(err);
    }
    setExcluindo(false);
  };

  const initialState = {
    name: "",
    number: "",
    email: "",
    banco: "",
    cpf: "",
    dataNascimento: "",
    prazo: "",
    parcela: "",
    valorLiberado: "",
    extraInfo: [],
  };

  const [contact, setContact] = useState(initialState);

  const [simulacoes, setSimulacoes] = useState([]);

  
  const [camposOcultos, setCamposOcultos] = useState([]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchContact = async () => {
      if (initialValues) {
        setContact((prevState) => ({ ...prevState, ...initialValues }));
      }

      if (!contactId) return;

      try {
        const { data } = await api.get(`/contacts/${contactId}`);
        if (isMounted.current) {
          const fetchedContact = { ...initialState, ...data };
          if (!fetchedContact.number && initialValues?.number) {
            fetchedContact.number = initialValues.number;
          }
          if (!fetchedContact.name && initialValues?.name) {
            fetchedContact.name = initialValues.name;
          }

          const standardMapping = [
            { key: "cpf", aliases: ["cpf", "clientcpf"] },
            {
              key: "dataNascimento",
              aliases: ["data de nascimento", "data nascimento", "datanascimento"],
            },
            { key: "prazo", aliases: ["prazo", "(clt) prazo", "(fgts) prazo"] },
            {
              key: "parcela",
              aliases: ["parcela", "(clt) parcela", "(fgts) parcela"],
            },
            {
              // "margem disponível" NAO entra aqui. Margem e quanto cabe por
              // mes na folha; valor liberado e quanto o cliente recebe. Com o
              // apelido da margem na lista, um contato do Presenca mostrava
              // R$ 442,45 (a margem) no lugar de R$ 4.003,07 (o liberado).
              key: "valorLiberado",
              aliases: [
                "valor liberado",
                "valor_liberado",
                "(clt) valor liberado",
                "(fgts) valor liberado",
              ],
            },
            {
              key: "banco",
              aliases: [
                "banco",
                "(clt) banco",
                "(fgts) banco",
                "bancos com oferta",
                "(clt) bancos com oferta",
                "(fgts) bancos com oferta",
              ],
            },
          ];

          const { simulacoes: sims, ocultos } = extrairSimulacoes(
            fetchedContact.extraInfo || []
          );
          setSimulacoes(sims);
          setCamposOcultos(ocultos);

          const remainingExtraInfo = [];
          const camposExatos = new Set();
          if (fetchedContact.extraInfo) {
            fetchedContact.extraInfo.forEach((info) => {
              const nameLower = (info.name || "").toLowerCase().trim();

              
              if (
                nameLower.endsWith("simulações (json)") ||
                nameLower.endsWith("simulacoes (json)")
              ) {
                return;
              }

              let matched = false;
              for (const item of standardMapping) {
                const exato = item.aliases.some((alias) => nameLower === alias);
                const porSufixo = item.aliases.some((alias) =>
                  nameLower.endsWith(alias)
                );

                if (!exato && !porSufixo) continue;

                const vazio =
                  !fetchedContact[item.key] || fetchedContact[item.key] === "";

                // O nome exato ("Valor Liberado") vale mais que o parecido
                // ("(CLT) Valor liberado") e sobrescreve. Sem isso, quem
                // ganhava era so quem estava mais cedo no array — e o campo
                // gravado pela proposta entra no fim da lista.
                if (vazio || exato) {
                  if (vazio || !camposExatos.has(item.key)) {
                    fetchedContact[item.key] = info.value;
                    if (exato) camposExatos.add(item.key);
                  }
                }

                matched = true;
                break;
              }
              if (!matched) {
                remainingExtraInfo.push(info);
              }
            });
          }

          if (fetchedContact.cpf) {
            fetchedContact.cpf = maskCPF(fetchedContact.cpf);
          }
          if (fetchedContact.number) {
            fetchedContact.number = maskPhone(fetchedContact.number);
          }
          if (fetchedContact.dataNascimento) {
            fetchedContact.dataNascimento = maskDate(fetchedContact.dataNascimento);
          }
          if (fetchedContact.parcela) {
            fetchedContact.parcela =
              moedaDoRegistro(fetchedContact.parcela) || fetchedContact.parcela;
          }
          if (fetchedContact.valorLiberado) {
            fetchedContact.valorLiberado =
              moedaDoRegistro(fetchedContact.valorLiberado) ||
              fetchedContact.valorLiberado;
          }

          fetchedContact.extraInfo = remainingExtraInfo;
          setContact(fetchedContact);
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchContact();
  }, [contactId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setContact(initialState);
    setSimulacoes([]);
    setCamposOcultos([]);
  };

  const handleSaveContact = async (values) => {
    const submitValues = { ...values };
    const standardInfo = [];
    if (submitValues.cpf) standardInfo.push({ name: "CPF", value: submitValues.cpf });
    if (submitValues.dataNascimento)
      standardInfo.push({ name: "Data de Nascimento", value: submitValues.dataNascimento });
    if (submitValues.prazo) standardInfo.push({ name: "Prazo", value: submitValues.prazo });
    if (submitValues.parcela) standardInfo.push({ name: "Parcela", value: submitValues.parcela });
    if (submitValues.valorLiberado)
      standardInfo.push({ name: "Valor Liberado", value: submitValues.valorLiberado });
    if (submitValues.banco) standardInfo.push({ name: "Banco", value: submitValues.banco });

    submitValues.extraInfo = [
      ...standardInfo,
      ...(submitValues.extraInfo || []),
      ...camposOcultos
    ];

    delete submitValues.cpf;
    delete submitValues.dataNascimento;
    delete submitValues.prazo;
    delete submitValues.parcela;
    delete submitValues.valorLiberado;
    delete submitValues.banco;

    if (submitValues.number) {
      submitValues.number = submitValues.number.replace(/\D/g, "");
    }
    if (!submitValues.number) {
      delete submitValues.number;
    }

    try {
      if (contactId) {

        
        submitValues.substituirExtraInfo = true;
        
        submitValues.nameLocked = true;

        const { data } = await api.put(`/contacts/${contactId}`, submitValues);
        handleClose();
      } else {
        const { data } = await api.post("/contacts", submitValues);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("contactModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      disableScrollLock={false}
      PaperProps={{ className: classes.dialogPaper }}
    >
      {/* Topo com a identidade do contato */}
      <Box className={classes.hero}>
        <Box className={classes.heroTop}>
          <Avatar src={contact.profilePicUrl || undefined} className={classes.avatar}>
            {(contact.name || "?").trim().charAt(0).toUpperCase()}
          </Avatar>
          <Box className={classes.heroTexto}>
            <Typography className={classes.heroEtiqueta}>
              {contactId ? "Ficha do contato" : "Novo contato"}
            </Typography>
            <Typography className={classes.heroNome}>
              {contact.name || (contactId ? "Sem nome" : "Cadastre um novo contato")}
            </Typography>
            <Box className={classes.heroChips}>
              {contact.number && (
                <span className={classes.heroChip}>
                  <WhatsAppIcon /> {contact.number}
                </span>
              )}
              {contact.cpf && (
                <span className={classes.heroChip}>
                  <CreditCardIcon /> {contact.cpf}
                </span>
              )}
              {contact.dataNascimento && (
                <span className={classes.heroChip}>
                  <EventIcon /> {contact.dataNascimento}
                </span>
              )}
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" className={classes.closeBtn}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Formik
        initialValues={contact}
        enableReinitialize={true}
        validationSchema={ContactSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            handleSaveContact(values);
            actions.setSubmitting(false);
          }, 300);
        }}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form style={{ display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}>
            <DialogContent className={classes.dialogContent}>
              {contactId && (
                <Box className={classes.statsGrid}>
                  {[
                    { rotulo: "Banco", valor: values.banco },
                    { rotulo: "Valor liberado", valor: values.valorLiberado },
                    { rotulo: "Parcela", valor: values.parcela },
                    { rotulo: "Prazo", valor: values.prazo ? `${String(values.prazo).replace(/x$/i, "")}x` : "" },
                  ].map((item) => (
                    <Box key={item.rotulo} className={classes.statCard}>
                      <Typography className={classes.statLabel}>{item.rotulo}</Typography>
                      <Typography
                        className={`${classes.statValue} ${item.valor ? "" : classes.statVazio}`}
                        title={item.valor || ""}
                      >
                        {item.valor || "—"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader}>
                  <Box className={classes.sectionIconBox}>
                    <PersonOutlineIcon className={classes.sectionIcon} />
                  </Box>
                  <Typography className={classes.sectionTitle}>
                    Dados do Contato
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Nome"
                      name="name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonOutlineIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="WhatsApp"
                      name="number"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      value={values.number || ""}
                      onChange={(e) => setFieldValue("number", maskPhone(e.target.value))}
                      error={touched.number && Boolean(errors.number)}
                      helperText={touched.number && errors.number}
                      placeholder="+55 (11) 99999-9999"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WhatsAppIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="E-mail"
                      name="email"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      placeholder="email@exemplo.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MailOutlineIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {}
              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader}>
                  <Box className={classes.sectionIconBox}>
                    <AccountBalanceIcon className={classes.sectionIcon} />
                  </Box>
                  <Typography className={classes.sectionTitle}>
                    Dados Comerciais & Propostas
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Banco"
                      name="banco"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      value={values.banco || ""}
                      onChange={(e) => setFieldValue("banco", e.target.value)}
                      placeholder="Ex: FACTA, PAN..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountBalanceIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="CPF"
                      name="cpf"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      value={values.cpf || ""}
                      onChange={(e) => setFieldValue("cpf", maskCPF(e.target.value))}
                      placeholder="000.000.000-00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CreditCardIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Data de Nascimento"
                      name="dataNascimento"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      value={values.dataNascimento || ""}
                      onChange={(e) =>
                        setFieldValue("dataNascimento", maskDate(e.target.value))
                      }
                      placeholder="DD/MM/AAAA"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EventIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Prazo (meses)"
                      name="prazo"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      value={values.prazo || ""}
                      onChange={(e) => setFieldValue("prazo", maskNumber(e.target.value))}
                      placeholder="Ex: 84"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScheduleIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Parcela"
                      name="parcela"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      value={values.parcela || ""}
                      onChange={(e) =>
                        setFieldValue("parcela", maskMoney(e.target.value))
                      }
                      placeholder="R$ 0,00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Valor Liberado"
                      name="valorLiberado"
                      variant="outlined"
                      size="small"
                      fullWidth
                      className={classes.customInput}
                      value={values.valorLiberado || ""}
                      onChange={(e) =>
                        setFieldValue("valorLiberado", maskMoney(e.target.value))
                      }
                      placeholder="R$ 0,00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MonetizationOnIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {simulacoes.length > 0 && (
                  <Box className={classes.simBox}>
                    <Typography className={classes.simTitle}>
                      Simulações dos bancos ({simulacoes.length})
                    </Typography>
                    <table className={classes.simTable}>
                      <thead>
                        <tr>
                          <th>Banco</th>
                          <th>Valor liberado</th>
                          <th>Parcela</th>
                          <th>Prazo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {simulacoes.map((sim, idx) => {
                          // Quando ha varias tabelas do mesmo banco, o que marca
                          // a linha escolhida e a flag vinda da simulacao. Sem
                          // ela (dados antigos), caimos no nome do banco.
                          const temFlag = simulacoes.some(
                            (s) => s.escolhida !== undefined
                          );
                          const escolhido = temFlag
                            ? sim.escolhida === true
                            : values.banco &&
                              sim.banco.toLowerCase() ===
                                String(values.banco).toLowerCase().trim();
                          return (
                            <tr
                              key={`${sim.banco}-${sim.tabela || idx}`}
                              className={escolhido ? classes.simEscolhido : ""}
                            >
                              <td>
                                {sim.banco}
                                {sim.tabela && (
                                  <span
                                    style={{
                                      display: "block",
                                      fontSize: "0.72rem",
                                      opacity: 0.65,
                                      fontWeight: 400,
                                    }}
                                  >
                                    {sim.tabela}
                                  </span>
                                )}
                                {escolhido && (
                                  <span className={classes.simTag}>
                                    escolhido
                                  </span>
                                )}
                              </td>
                              <td>
                                {sim.valor ? (
                                  comMoeda(sim.valor)
                                ) : sim.margem ? (
                                  <span style={{ opacity: 0.65 }}>
                                    margem {comMoeda(sim.margem)}
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>{sim.parcela ? comMoeda(sim.parcela) : "—"}</td>
                              <td>
                                {sim.prazo
                                  ? `${String(sim.prazo).replace(/x$/i, "")}x`
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                )}
              </Box>

              {}
              <Box className={classes.sectionCard}>
                <Box className={classes.sectionHeader}>
                  <Box className={classes.sectionIconBox}>
                    <InfoOutlinedIcon className={classes.sectionIcon} />
                  </Box>
                  <Typography className={classes.sectionTitle}>
                    Informações Adicionais (IA e Observações)
                  </Typography>
                </Box>

                <FieldArray name="extraInfo">
                  {({ push, remove }) => (
                    <>
                      {values.extraInfo && values.extraInfo.length > 0 ? (
                        values.extraInfo.map((info, index) => (
                          <div className={classes.extraInfoRow} key={`${index}-info`}>
                            <Field
                              as={TextField}
                              label="Campo"
                              name={`extraInfo[${index}].name`}
                              variant="outlined"
                              size="small"
                              className={classes.customInput}
                              style={{ flex: 1 }}
                            />
                            <Field
                              as={TextField}
                              label="Valor"
                              name={`extraInfo[${index}].value`}
                              variant="outlined"
                              size="small"
                              className={classes.customInput}
                              style={{ flex: 1.5 }}
                            />
                            <IconButton
                              size="small"
                              className={classes.deleteBtn}
                              onClick={() => remove(index)}
                              title="Remover campo"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </div>
                        ))
                      ) : (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ fontStyle: "italic", marginBottom: 8 }}
                        >
                          Nenhuma informação adicional cadastrada.
                        </Typography>
                      )}

                      {poderDeAdmin(user?.profile) && (
                        <Button
                          className={classes.btnAddExtra}
                          startIcon={<AddIcon />}
                          onClick={() => push({ name: "", value: "" })}
                        >
                          Adicionar informação
                        </Button>
                      )}
                    </>
                  )}
                </FieldArray>
              </Box>
            </DialogContent>

            {}
            <DialogActions className={classes.dialogActions}>
              {contactId && (
                <Can
                  role={user.profile}
                  perform="contacts-page:deleteContact"
                  yes={() => (
                    <Button
                      onClick={excluirContato}
                      disabled={isSubmitting || excluindo}
                      startIcon={<DeleteOutlineIcon />}
                      className={confirmarExclusao ? classes.btnExcluirConfirma : classes.btnExcluir}
                    >
                      {excluindo
                        ? "Excluindo..."
                        : confirmarExclusao
                        ? "Clique de novo para excluir"
                        : "Excluir contato"}
                    </Button>
                  )}
                />
              )}
              <div style={{ flex: 1 }} />
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                variant="outlined"
                className={classes.btnCancel}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                className={`${classes.btnSave} ${classes.btnWrapper}`}
              >
                Salvar Contato
                {isSubmitting && (
                  <CircularProgress
                    size={20}
                    className={classes.buttonProgress}
                  />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default ContactModal;
